﻿import "regenerator-runtime/runtime";
import React, { useState, Fragment, useCallback, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import * as IProduct from './Interfaces'
import { CartTotal } from "./CartTotal";
import { useRouteMatch, useHistory } from "react-router-dom";
import { SidebarFilterNav, CollapseFilterNav, MobileTopNav, SidebarBadgeFilters, MobileBadgeFilters } from "./FilterNav";
import { ShopCountdown } from "./ShopCountdown";
import { calculateTotals, syncCart, filterProductsByCategories, filterProductsBySupplier, filterProductsByNewOrFeatured, getShopWindowLabel } from "./ShopFunctions";
import { ProductPage } from "./product/ProductPage";
import { Product } from "./product/Product";
import moment from "moment";
import { slugify } from "../../helpers/Formatters";

export function Shop(props: { initial: IProduct.ShopData; cartNav: HTMLElement }) {
    const now = new Date();
    const openWindows = props.initial.windows.filter(x => new Date(x.opens) <= now && new Date(x.closes) > now);
    
    const [carts, setCarts] = useState(props.initial.carts);
    const [shopWindow, setShopWindow] = useState(() => {
        if (carts.length > 0) {
            const freshestOpenWindow = openWindows.find(x => x.windowId == carts[0].windowId);
            return freshestOpenWindow ?? props.initial.current;            
        }
    });
    const [time, setTime] = useState(new Date());
    const [badgeFilters, setBadgeFilters] = useState<number[]>([]);

    const cart = shopWindow ? carts.find(x => x.windowId == shopWindow.windowId) : { windowId: 0, status: 'Dummy', dateOrdered: null, products: [] };
    const shopOpen = shopWindow?.opens && new Date(shopWindow.opens) <= now && shopWindow?.closes && new Date(shopWindow.closes) > now;
    const totals = calculateTotals(props.initial.products, props.initial.vatRate, props.initial.clubVatRegistered, shopWindow?.marketDayFee ?? 0, cart);

    const history = useHistory();
    const searchParams = new URLSearchParams(history.location.search);

    useEffect(() => { //storage listener, receive cart modification events from other pages
        const listener = (e: StorageEvent) => {
            if (e.key === null) { //store cleared
                setCarts(carts => carts.map(x => ({ ...x, products: [] })));
            }
            else if (e.key === '_fhcart') {
                const storageCarts = JSON.parse(e.newValue) as IProduct.ShopCart[];

                setCarts(storageCarts);
            }
        };
        window.addEventListener('storage', listener);

        return () => { window.removeEventListener('storage', listener); };
    });

    const itemCountChanged = useCallback((productId: number, variantId: number, qty: number) => {
        setCarts(carts => {
            const stateCart = carts.find(x => x.windowId === cart.windowId);
            const updatedCart = {
                ...stateCart, products: [...stateCart.products.filter(y => y.productVariantId !== variantId), { productVariantId: variantId, quantity: qty }]
            }
            const updatedCarts = carts.map(x => x === stateCart ? updatedCart : x);
            syncCart(updatedCarts, updatedCart, false);

            return updatedCarts;
        });
    }, [cart]);

    const repeatOrder = () => {
        setCarts(carts => {
            const updatedCart = { ...cart, products: props.initial.previousCart.products };
            const updatedCarts = carts.map(x => x !== cart ? x : updatedCart);            

            syncCart(updatedCarts, updatedCart, false);

            return updatedCarts;
        });
    }

    useEffect(() => {
        const shopNav = document.getElementById('shopNavLink');
        shopNav.onclick = () => { history.push('/Shop'); return false; };
    }, []);

    useEffect(() => {
        if (window.scrollY > 146) {
            window.scrollTo(0, 146);
        }
    }, [history.location.pathname]);

    const refresh = () => {
        setTime(new Date());
    }

    const productMatch = useRouteMatch({ path: "/Shop/Product/:title", strict: true, sensitive: false });    

    if (productMatch) {
        const titleParts = productMatch.params['title'].split('-');

        if (titleParts.length > 0 && parseInt(titleParts[0], 10)) {
            const titleId = parseInt(titleParts[0], 10);

            const product = props.initial.products.find(x => x.id === titleId);

            if (product) {
                return (
                    <Fragment>
                        <div className="row">
                            <div className="col-auto bg-white d-none d-md-block pl-0 pt-2" id="shop-nav-sidebar">
                                <SidebarBadgeFilters badges={props.initial.badges} filters={badgeFilters} updateFilters={setBadgeFilters} />
                                <SidebarFilterNav categories={props.initial.categories} suppliers={props.initial.suppliers} />
                            </div>
                            <div className="col">
                                <div className="d-lg-flex d-block flex-row-reverse align-items-center mb-3" style={{ gridGap: '1rem' }}>
                                    {openWindows.length > 1 ? <div className="window-selector"><select className="custom-select mb-1 mb-lg-0" id="chooseWindow" value={shopWindow.windowId} onChange={(e) => setShopWindow(props.initial.windows.find(x => x.windowId === parseInt(e.target.value, 10)))}>
                                        {openWindows.map(x => <option value={x.windowId}>Market: {getShopWindowLabel(x)}</option>)}
                                    </select></div> : null}
                                    <ShopCountdown current={shopWindow} next={props.initial.next} refresh={refresh} />
                                </div>

                                <ProductPage product={product} badges={props.initial.badges} itemCountChanged={itemCountChanged} cart={cart} shopOpen={shopOpen} loggedIn={props.initial.loggedIn}/>
                            </div>
                        </div>
                        {props.cartNav ? ReactDOM.createPortal(<CartTotal items={totals.items} total={totals.itemTotal} />, props.cartNav) : null}
                        {ReactDOM.createPortal(<CollapseFilterNav categories={props.initial.categories} suppliers={props.initial.suppliers} />, document.getElementById('extra-nav-root'))}
                    </Fragment>
                );
            }
        }

        history.push('/Shop'); //couldn't find product, return to shop
    }

    const subcategoryFilterMatch = useRouteMatch({ path: "/Shop/Category/:category/:subcategory", strict: true, sensitive: false });
    const categoryFilterMatch = useRouteMatch({ path: "/Shop/Category/:category", strict: true, sensitive: false });
    const supplierFilterMatch = useRouteMatch({ path: "/Shop/Supplier/:supplier", strict: true, sensitive: false });
    const searchFilter = searchParams.has('search') ? decodeURIComponent(searchParams.get('search')) : null;
    const sortOrder = searchParams.has('sort') ? decodeURIComponent(searchParams.get('sort')) : 'alphabetical';

    let shownProducts: IProduct.Product[];
    let productsTitle: string;

    if (searchFilter && searchFilter.length > 2) {
        const terms = searchFilter.toLowerCase().split(' ');
        shownProducts = props.initial.products;
        terms.forEach((term) => { shownProducts = shownProducts.filter((x) => x.name.toLowerCase().includes(term)) });
        productsTitle = 'Search Results';
    }
    else if (subcategoryFilterMatch) {
        const category = props.initial.categories.find(x => slugify(x.name) === subcategoryFilterMatch.params['category']);
        if (category) {
            const subCategory = category.subcategories.find(x => slugify(x.name) === subcategoryFilterMatch.params['subcategory']);            

            if (subCategory) {
                shownProducts = filterProductsByCategories(props.initial.products, [subCategory.id]);
                productsTitle = subCategory.name;
            }
        }
    }
    else if (categoryFilterMatch) {
        const category = props.initial.categories.find(x => slugify(x.name) === categoryFilterMatch.params['category']);
        if (category) {
            const ids = category.subcategories.map(x => x.id);

            shownProducts = filterProductsByCategories(props.initial.products, ids);
            productsTitle = category.name;
        }
    }
    else if (supplierFilterMatch) {
        ({ title: productsTitle, products: shownProducts } = filterProductsBySupplier(props.initial.products, props.initial.suppliers, supplierFilterMatch.params['supplier']));
    }
    else if (badgeFilters.length > 0) {
        shownProducts = props.initial.products;
        productsTitle = "Food Heroes";
    }
    else {
        shownProducts = filterProductsByNewOrFeatured(props.initial.products, false, true);
        productsTitle = 'New & Featured';
    }

    if (badgeFilters.length > 0) {
        shownProducts = shownProducts.filter(x => x.badges.some(y => badgeFilters.includes(y)));
    }

    switch (sortOrder) {
        case 'alphabetical': shownProducts.sort((x, y) => { const xKey = x.name.toLowerCase(); const yKey = y.name.toLowerCase(); return xKey < yKey ? -1 : xKey === yKey ? 0 : 1 }); break;
        case 'popularity': shownProducts.sort((x, y) => { const xKey = x.popularity; const yKey = y.popularity; return xKey < yKey ? -1 : xKey === yKey ? 0 : 1 }); break;
        case 'date': shownProducts.sort((x, y) => { const xKey = x.age; const yKey = y.age; return xKey < yKey ? -1 : xKey === yKey ? 0 : 1 }).reverse(); break;
        case 'price': shownProducts.sort((x, y) => {
            const xKey = Math.min(...x.variants.map(z => x.pricedByWeight ? z.pricePerKg : z.price)); const yKey = Math.min(...y.variants.map(z => y.pricedByWeight ? z.pricePerKg : z.price)); return xKey < yKey ? -1 : xKey === yKey ? 0 : 1
        }); break;
        case 'price-desc': shownProducts.sort((x, y) => {
            const xKey = Math.min(...x.variants.map(z => x.pricedByWeight ? z.pricePerKg : z.price)); const yKey = Math.min(...y.variants.map(z => y.pricedByWeight ? z.pricePerKg : z.price)); return xKey < yKey ? -1 : xKey === yKey ? 0 : 1
        }).reverse(); break;
    }

    const updateQuery = (history, searchFilter: string, sortOrder: string) => {
        const params = [];
        if (searchFilter)
            params.push(`search=${encodeURIComponent(searchFilter)}`);
        if (sortOrder)
            params.push(`sort=${encodeURIComponent(sortOrder)}`);

        if (params.length === 0) {
            history.push(history.location.pathname);
        }
        if (params.length > 0) {
            const query = params.join('&');
            history.push(`${history.location.pathname}?${query}`);
        }
    }

    return (
        <>
            <div className="row">
                <div className="col-auto bg-white d-none d-md-block pl-0 pt-2" id="shop-nav-sidebar">
                    <SidebarBadgeFilters badges={props.initial.badges} filters={badgeFilters} updateFilters={setBadgeFilters} />
                    <SidebarFilterNav categories={props.initial.categories} suppliers={props.initial.suppliers} />
                </div>
                <div className="col pb-3">
                    {props.initial.preview ? <div className="alert alert-info" role="alert">
                        You are viewing a preview of the shop. All available products and suppliers are being shown. You may see products here that are not in your shop or visible by your members.
                    </div> : null}
                    <div className="d-lg-flex d-block flex-row-reverse align-items-center mb-3" style={{ gridGap: '1rem' }}>
                        {openWindows.length > 1 ? <div className="window-selector"><select className="custom-select mb-1 mb-lg-0" id="chooseWindow" value={shopWindow.windowId} onChange={(e) => setShopWindow(props.initial.windows.find(x => x.windowId === parseInt(e.target.value, 10)))}>
                            {openWindows.map(x => <option value={x.windowId}>{getShopWindowLabel(x)}</option>)}
                        </select></div> : null}
                        <ShopCountdown current={shopWindow} next={props.initial.next} refresh={refresh} />                                                
                    </div>                    
                    {totals.items === 0 && cart.status === 'Active' && !props.initial.preview && props.initial.previouslyOrdered ? <div className="alert alert-secondary" role="alert">
                        <p>You last ordered on {moment(props.initial.previouslyOrdered).format('D MMMM')}. Would you like to add these items to your cart again? You can continue shopping and amend your cart afterwards.</p>
                        <button className="btn btn-primary" onClick={() => repeatOrder()}>Repeat Order</button>
                    </div> : null}
                    {cart.status === 'Ordered' ? <div className="alert alert-warning" role="alert">
                        You have already placed an order. {shopOpen ? <span>To continue shopping and allow amending your cart, you must first withdraw your order <a href='/Shop/Cart'>here</a>.</span> : null}
                    </div> : null}

                    <div className="d-block d-md-none mb-3">
                        <MobileBadgeFilters badges={props.initial.badges} filters={badgeFilters} updateFilters={setBadgeFilters} />
                        <MobileTopNav categories={props.initial.categories} suppliers={props.initial.suppliers} />
                    </div>
                    <div id="shop-filters" className="mb-3">
                        <input type="text" value={searchFilter} className="form-control" placeholder="search..." onChange={(e) => updateQuery(history, e.target.value, sortOrder)} />

                        <select className="custom-select" value={sortOrder} onChange={(e) => updateQuery(history, searchFilter, e.target.value)}>
                            <option value="alphabetical">Sort - alphabetical</option>
                            <option value="popularity">Sort - popularity</option>
                            <option value="date">Sort - latest</option>
                            <option value="price">Sort - price asc.</option>
                            <option value="price-desc">Sort - price desc.</option>
                        </select>
                    </div>
                    <h3 className="d-block d-md-none pb-3 text-center">{productsTitle}</h3>
                    <div id="shop">
                        {shownProducts.length > 0 ? shownProducts.map((p) => <Product key={p.id} badges={props.initial.badges} product={p} itemCountChanged={itemCountChanged} cart={cart} shopOpen={shopOpen} loggedIn={props.initial.loggedIn} />) : <p className="text-white">No matching products</p>}
                    </div>
                </div>
            </div>
            {props.cartNav ? ReactDOM.createPortal(<CartTotal items={totals.items} total={totals.itemTotal} />, props.cartNav) : null}
            {ReactDOM.createPortal(<CollapseFilterNav categories={props.initial.categories} suppliers={props.initial.suppliers} />, document.getElementById('extra-nav-root'))}
        </>
    );  
}